import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, message } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import FileUpload from "../FileUpload";
import { createFolderUploadState } from "../../services/documentsAPI";

const getFileDisplayPath = (file) =>
  file.relativePath ? `${file.relativePath}/${file.name}` : file.name;

const UploadFileModal = ({
  visible,
  onCancel,
  onOk,
  loading,
  allowFolders = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const uploadStateRef = useRef(null);
  const submittingRef = useRef(false);
  const busy = loading || submitting;

  useEffect(() => {
    if (!visible) {
      setSelectedFiles([]);
      uploadStateRef.current = null;
    }
  }, [visible]);

  const handleFileSelect = (files) => {
    const newFiles = Array.isArray(files) ? files : [files];
    setSelectedFiles((prevFiles) => {
      const existingFilePaths = new Set(prevFiles.map(getFileDisplayPath));
      const uniqueNewFiles = newFiles.filter(
        (file) => !existingFilePaths.has(getFileDisplayPath(file)),
      );
      return [...prevFiles, ...uniqueNewFiles];
    });
  };

  const handleUpload = async () => {
    if (submittingRef.current || loading) return;
    if (selectedFiles.length === 0) {
      message.warning("Please select at least one file");
      return;
    }
    if (!uploadStateRef.current) {
      uploadStateRef.current = createFolderUploadState();
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onOk(selectedFiles, uploadStateRef.current);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    onCancel();
  };

  const handleDeleteFile = (indexToDelete) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToDelete),
    );
  };

  // Format bytes to human readable format
  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Modal
      title={allowFolders ? "Upload Folder or Files" : "Upload Files"}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={busy}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || busy}
          icon={busy ? <LoadingOutlined /> : <UploadOutlined />}
        >
          {busy ? "Uploading" : "Upload"}
        </Button>,
      ]}
      width={640}
    >
      <p className="text-gray-500 mb-4">Upload your attachments here.</p>

      <FileUpload
        onUpload={handleFileSelect}
        accept="*/*"
        maxSize={262144000}
        multiple={true}
        allowFolders={allowFolders}
      />

      {selectedFiles.length > 0 && (
        <div className="mt-4">
          <p className="text-gray-700 font-medium">
            Selected Files ({selectedFiles.length}):
          </p>
          <ul className="mt-2 max-h-64 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <li
                key={index}
                className="flex items-center justify-between py-1 text-gray-600"
              >
                <span className="truncate flex-1">
                  {getFileDisplayPath(file)} ({formatBytes(file.size)})
                </span>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteFile(index)}
                  className="text-red-500 hover:text-red-700"
                  disabled={loading}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
};

export default UploadFileModal;
